import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { fromCents } from '@ankor-io/common/domain/pay/currency'
import type { TransactionVolumePayload } from '@ankor-io/common/types/lighthouse'
import { formatCompactMoney, formatCompactNumber } from '@ankor-io/common/util/numbers'
import { Heading, Loading, Text } from '@ankor-io/ui-components'

import { useTransactionVolumeQuery } from '@/queries'

type VolumeKindKey = keyof TransactionVolumePayload

// Colours mirror the transactions-stats line chart for cross-chart consistency.
const VOLUME_KINDS: { key: VolumeKindKey; label: string; colour: string }[] = [
  { key: 'cardTransactions', label: 'Card transactions', colour: '#3b82f6' },
  { key: 'deposits', label: 'Deposits', colour: '#22c55e' },
  { key: 'payouts', label: 'Payouts', colour: '#f97316' },
  { key: 'conversions', label: 'Conversions', colour: '#eab308' },
  { key: 'walletTransfers', label: 'Wallet transfers', colour: '#a855f7' },
]

type CurrencyVolumeRow = Record<string, string | number>

type VolumeChartEntry = { value: number; name: string; color: string }

const VolumeChartTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: VolumeChartEntry[]
  label?: string
}) => {
  if (!active || !payload || payload.length === 0) return null

  return (
    <div
      role="tooltip"
      className="min-w-44 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-lg dark:border-gray-600 dark:bg-gray-800"
    >
      <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
      <dl className="mt-2 space-y-1">
        {payload
          .filter((entry) => entry.value !== undefined && entry.value !== 0)
          .map((entry) => (
            <div
              key={entry.name}
              className="flex items-baseline justify-between gap-4 text-sm"
            >
              <dt style={{ color: entry.color }}>{entry.name}</dt>
              <dd
                className="tabular-nums font-medium"
                style={{ color: entry.color }}
              >
                {formatCompactMoney(entry.value, label)}
              </dd>
            </div>
          ))}
      </dl>
    </div>
  )
}

export const TransactionVolume = () => {
  const { data, isPending, isError, error } = useTransactionVolumeQuery()

  if (isPending) {
    return (
      <div data-testid="transaction-volume-loading">
        <Loading variant="spinner" />
      </div>
    )
  }

  if (isError) {
    return (
      <Text
        className="text-red-600 dark:text-red-400"
        data-testid="transaction-volume-error"
      >
        Unable to load transaction volume: {error.message}
      </Text>
    )
  }

  // One bar per transaction kind, grouped by currency. Currencies are ordered
  // by total volume (largest first) so the biggest flows read immediately.
  const totalByCurrency = new Map<string, number>()
  for (const kind of VOLUME_KINDS) {
    for (const volume of data[kind.key]) {
      totalByCurrency.set(volume.currencyCode, (totalByCurrency.get(volume.currencyCode) ?? 0) + volume.amountCents)
    }
  }
  const currencies = [...totalByCurrency.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([currencyCode]) => currencyCode)

  const chartData: CurrencyVolumeRow[] = currencies.map((currencyCode) => {
    const row: CurrencyVolumeRow = { currencyCode }
    for (const kind of VOLUME_KINDS) {
      const volume = data[kind.key].find((entry) => entry.currencyCode === currencyCode)
      row[kind.key] = fromCents(volume?.amountCents ?? 0)
    }
    return row
  })

  return (
    <section
      aria-labelledby="transaction-volume-heading"
      className="space-y-3"
    >
      <Heading level={2}>Transaction volume</Heading>
      <Text className="text-gray-600 dark:text-gray-400">
        Transaction volume by currency across the five transaction types over the last 7 days.
      </Text>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Volume by currency on the left */}
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-2"
          data-testid="transaction-volume"
        >
          {VOLUME_KINDS.map((kind) => (
            <div
              key={kind.key}
              className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                {kind.label}
              </p>
              {data[kind.key].length === 0 ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No activity in the last 7 days</p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {[...data[kind.key]]
                    .sort((a, b) => b.amountCents - a.amountCents)
                    .map((volume) => (
                      <li
                        key={volume.currencyCode}
                        className="flex items-baseline justify-between gap-2 text-sm"
                      >
                        <span className="text-gray-700 dark:text-gray-300">{volume.currencyCode}</span>
                        <span className="tabular-nums text-xl text-font-medium text-gray-900 dark:text-white">
                          {formatCompactMoney(fromCents(volume.amountCents))}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* All kinds by currency on the right */}
        <div
          className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 lg:col-span-3"
          data-testid="transaction-volume-chart"
        >
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Volume by currency — last 7 days
            </p>
            <div
              className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-700 dark:text-gray-300"
              aria-hidden
            >
              {VOLUME_KINDS.map((kind) => (
                <span
                  key={kind.key}
                  className="flex items-center gap-2"
                >
                  <span
                    className="inline-block h-2 w-4 shrink-0 rounded-full"
                    style={{ backgroundColor: kind.colour }}
                  />
                  {kind.label}
                </span>
              ))}
            </div>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={chartData}
                margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
                barGap={2}
              >
                <XAxis
                  dataKey="currencyCode"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                  tickFormatter={(value: number) => formatCompactNumber(Number(value))}
                />
                <Tooltip content={<VolumeChartTooltip />} />
                {VOLUME_KINDS.map((kind) => (
                  <Bar
                    key={kind.key}
                    dataKey={kind.key}
                    name={kind.label}
                    fill={kind.colour}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={32}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  )
}
